import React, { useMemo, useState } from 'react';
import './App.css';
import { apiUrl } from './config';

function normalizeTrack(track) {
  if (!track || !track.id || !track.uri) return null;
  return {
    id: track.id,
    uri: track.uri,
    name: track.name,
    subtitle: (track.artists || []).map(a => a.name).filter(Boolean).join(', '),
    image: track.album?.images?.[2]?.url || track.album?.images?.[0]?.url || null,
    kind: 'track'
  };
}

function normalizePlaylist(playlist) {
  if (!playlist || !playlist.id || !playlist.uri) return null;
  return {
    id: playlist.id,
    uri: playlist.uri,
    name: playlist.name,
    subtitle: playlist.owner?.display_name ? `Playlist • ${playlist.owner.display_name}` : 'Playlist',
    image: playlist.images?.[2]?.url || playlist.images?.[0]?.url || null,
    kind: 'playlist'
  };
}

function normalizeAlbum(album) {
  if (!album || !album.id || !album.uri) return null;
  return {
    id: album.id,
    uri: album.uri,
    name: album.name,
    subtitle: (album.artists || []).map(a => a.name).filter(Boolean).join(', ') || 'Album',
    image: album.images?.[2]?.url || album.images?.[0]?.url || null,
    kind: 'album'
  };
}

function SpotifySearch({ deviceId, disabled, variant = 'default', onPlayed }) {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('both'); // 'track' | 'playlist' | 'album' | 'both'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [playingUri, setPlayingUri] = useState(null);

  const typeParam = useMemo(() => {
    if (mode === 'track') return 'track';
    if (mode === 'playlist') return 'playlist';
    if (mode === 'album') return 'album';
    return 'track,playlist,album';
  }, [mode]);

  const canSearch = !disabled && query.trim().length > 1;

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const resp = await fetch(apiUrl(`/spotify/search?q=${encodeURIComponent(q)}&type=${encodeURIComponent(typeParam)}&limit=12`));
      const contentType = resp.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await resp.json()
        : await resp.text();
      if (!resp.ok) {
        // If the server returns HTML (e.g. "Cannot GET ..."), show a helpful message.
        if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
          throw new Error('spotify_search_endpoint_not_found');
        }
        throw new Error((data && data.error) || 'search_failed');
      }

      const list = [];
      if (Array.isArray(data.tracks)) list.push(...data.tracks.map(normalizeTrack));
      if (Array.isArray(data.playlists)) list.push(...data.playlists.map(normalizePlaylist));
      if (Array.isArray(data.albums)) list.push(...data.albums.map(normalizeAlbum));
      setResults(list.filter(Boolean));
    } catch (err) {
      setError(err?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const play = async (item) => {
    if (!item?.uri) return;
    setPlayingUri(item.uri);
    setError(null);
    try {
      const resp = await fetch(apiUrl('/spotify/play'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId,
          uri: item.uri,
          kind: item.kind
        })
      });
      const contentType = resp.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await resp.json()
        : await resp.text();
      if (!resp.ok) {
        if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
          throw new Error('spotify_play_endpoint_not_found');
        }
        throw new Error((data && data.error) || 'play_failed');
      }
      if (onPlayed) onPlayed(item);
    } catch (err) {
      setError(err?.message || 'Could not start playback');
    } finally {
      // leave playingUri highlighted briefly if desired; for now just clear
      setTimeout(() => setPlayingUri(null), 800);
    }
  };

  return (
    <div className={`spotify-search-container ${variant === 'compact' ? 'compact' : ''}`}>
      {variant !== 'compact' && (
        <div className="spotify-search-header">
          <div className="spotify-search-title">Spotify Search</div>
          <div className="spotify-search-modes">
            <button
              type="button"
              className={`spotify-search-mode ${mode === 'track' ? 'active' : ''}`}
              onClick={() => setMode('track')}
              disabled={disabled}
            >
              Songs
            </button>
            <button
              type="button"
              className={`spotify-search-mode ${mode === 'playlist' ? 'active' : ''}`}
              onClick={() => setMode('playlist')}
              disabled={disabled}
            >
              Playlists
            </button>
            <button
              type="button"
              className={`spotify-search-mode ${mode === 'album' ? 'active' : ''}`}
              onClick={() => setMode('album')}
              disabled={disabled}
            >
              Albums
            </button>
            <button
              type="button"
              className={`spotify-search-mode ${mode === 'both' ? 'active' : ''}`}
              onClick={() => setMode('both')}
              disabled={disabled}
            >
              All
            </button>
          </div>
        </div>
      )}

      <form className="spotify-search-form" onSubmit={runSearch}>
        <input
          className="spotify-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={disabled ? 'Login to search Spotify…' : 'Search songs, playlists, or albums…'}
          disabled={disabled}
        />
        <button className="spotify-search-btn" type="submit" disabled={!canSearch || loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="spotify-search-error">
          {error === 'not_authenticated'
            ? 'Please login with Spotify first.'
            : error === 'spotify_search_endpoint_not_found'
              ? 'Spotify search endpoint not found. Restart the backend server (npm run server) and refresh.'
              : error === 'spotify_play_endpoint_not_found'
                ? 'Spotify play endpoint not found. Restart the backend server (npm run server) and refresh.'
            : error}
        </div>
      )}

      {results.length > 0 && (
        <div className="spotify-search-results">
          {results.map((item) => (
            <div key={`${item.kind}:${item.id}`} className="spotify-search-result">
              {item.image ? (
                <img className="spotify-search-thumb" src={item.image} alt="" />
              ) : (
                <div className="spotify-search-thumb placeholder">{item.kind === 'playlist' ? '≡' : item.kind === 'album' ? '◷' : '♫'}</div>
              )}
              <div className="spotify-search-result-info">
                <div className="spotify-search-result-title">{item.name}</div>
                <div className="spotify-search-result-subtitle">{item.subtitle}</div>
              </div>
              <button
                type="button"
                className="spotify-search-play-btn"
                onClick={() => play(item)}
                disabled={disabled}
                title={deviceId ? 'Play on PomoDucky Player' : 'Play (select PomoDucky Player as device if needed)'}
              >
                {playingUri === item.uri ? '…' : 'Play'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SpotifySearch;

